import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { CostLine } from '@fleet/shared';
import { euro, fmtDate } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { AutoLink, PersoonLink } from '../../components/ui/EntiteitLink';
import { Panel } from '../../components/ui/Panel';
import { SectieKop } from '../../components/ui/SectieKop';
import { ApiError } from '../../lib/apiClient';
import { PersoonKosten } from '../personen/PersoonKosten';
import { useArchiveerPas, useDearchiveerPas, usePasDetail } from './passen.api';

const nf = (n: number) => n.toLocaleString('nl-NL', { maximumFractionDigits: 0 });

function StatCard({
  label,
  value,
  sub,
  color = '#111111',
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <Panel padding={16}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#666666',
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 900, marginTop: 6, color }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>{sub}</div>}
    </Panel>
  );
}

/** Consumption per unit (liters / kWh) summed over the pass's transactions. */
function verbruikLabel(costLines: CostLine[]): string {
  let liters = 0;
  let kwh = 0;
  for (const l of costLines) {
    if (!l.hoeveelheid || !l.eenheid) continue;
    const u = l.eenheid.toUpperCase();
    if (u.startsWith('LTR') || u.startsWith('LITER')) liters += l.hoeveelheid;
    else if (u.startsWith('KWH')) kwh += l.hoeveelheid;
  }
  const parts: string[] = [];
  if (liters > 0) parts.push(`${nf(liters)} L`);
  if (kwh > 0) parts.push(`${nf(kwh)} kWh`);
  return parts.length ? parts.join(' · ') : '—';
}

const TCOLS = '100px 60px 1fr 120px 100px 110px 1fr';

export function PasDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePasDetail(id);
  const archiveer = useArchiveerPas();
  const dearchiveer = useDearchiveerPas();

  const [confirm, setConfirm] = useState(false);
  const [archErr, setArchErr] = useState('');

  if (isError) return <div style={{ color: '#8A4029' }}>Pas niet gevonden.</div>;
  if (isLoading || !data) return <div style={{ color: '#888888' }}>Laden…</div>;

  const { pas, costLines, koppelingen, geredenKm } = data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/passen')}>
            ← Alle passen
          </Button>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            <h2
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 900,
                color: '#111111',
                fontFamily: "'Roboto Mono', monospace",
              }}
            >
              {pas.pasnummer}
            </h2>
            {!pas.actief && <Badge tone="neutral">Gearchiveerd</Badge>}
            {pas.fictief && <Badge tone="warning">Fictief</Badge>}
            {pas.persoonId ? (
              <Badge tone="success">Gekoppeld</Badge>
            ) : (
              <Badge tone="neutral">Ongekoppeld</Badge>
            )}
          </div>
          <div
            style={{
              fontSize: 14,
              color: '#666666',
              marginTop: 6,
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <span>
              Persoon:{' '}
              {pas.persoonNaam ? (
                <PersoonLink persoonId={pas.persoonId} naam={pas.persoonNaam} />
              ) : (
                <strong style={{ color: '#111111' }}>—</strong>
              )}
            </span>
            <span>
              Kenteken:{' '}
              {pas.kenteken ? (
                <AutoLink kenteken={pas.kenteken} />
              ) : (
                <strong style={{ color: '#111111' }}>—</strong>
              )}
            </span>
          </div>
        </div>
        <div>
          {pas.actief ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setArchErr('');
                setConfirm(true);
              }}
            >
              Archiveren
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="sm"
              disabled={dearchiveer.isPending}
              onClick={() => dearchiveer.mutate(pas.id)}
            >
              Terug in gebruik
            </Button>
          )}
        </div>
      </div>

      <SectieKop titel="Kosten pas" />

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12,
        }}
      >
        <StatCard
          label="Kosten totaal"
          value={pas.kostenTotaal ? euro(pas.kostenTotaal) : '—'}
          sub="excl. btw"
        />
        <StatCard
          label="Deze maand"
          value={pas.kostenDezeMaand ? euro(pas.kostenDezeMaand) : '—'}
          color="#2A6085"
        />
        <StatCard label="Transacties" value={String(pas.aantalTransacties)} />
        <StatCard
          label="Laatst gebruikt"
          value={pas.laatsteTransactie ? fmtDate(pas.laatsteTransactie) : '—'}
        />
        <StatCard label="Verbruik" value={verbruikLabel(costLines)} sub="liters / kWh" />
        {/* Kilometers op deze pas: de spanne tussen zijn laagste en hoogste
            betrouwbare tellerstand. Een pas die alleen heeft geladen heeft er geen,
            want een laadpaal vraagt de stand niet. */}
        <StatCard
          label="Gereden"
          value={geredenKm ? `${geredenKm.toLocaleString('nl-NL')} km` : '—'}
          sub={geredenKm ? 'tussen eerste en laatste stand' : 'geen tellerstanden'}
        />
      </div>

      {/* Cost breakdown (reused) */}
      <PersoonKosten costLines={costLines} />

      <SectieKop titel="Transacties" />
      <Panel style={{ padding: '20px 24px' }}>
        {costLines.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888888' }}>Nog geen transacties.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: TCOLS,
                gap: 10,
                padding: '8px 0',
                borderBottom: '2px solid #111111',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#666666',
                minWidth: 780,
              }}
            >
              <span>Datum</span>
              <span>Tijd</span>
              <span>Categorie / product</span>
              <span>Hoeveelheid</span>
              <span>Prijs</span>
              <span style={{ textAlign: 'right' }}>Bedrag</span>
              <span>Locatie</span>
            </div>
            <div style={{ maxHeight: 420, overflowY: 'auto' }}>
              {costLines.map((l) => (
                <div
                  key={l.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: TCOLS,
                    gap: 10,
                    padding: '8px 0',
                    borderBottom: '1px solid #F0E8DC',
                    fontSize: 13,
                    alignItems: 'center',
                    minWidth: 780,
                  }}
                >
                  <span style={{ color: '#666666' }}>{l.datum ? fmtDate(l.datum) : '—'}</span>
                  <span style={{ color: '#888888' }}>{l.tijd ?? ''}</span>
                  <span>
                    {l.categorie}
                    {l.product && <span style={{ color: '#888888' }}> · {l.product}</span>}
                  </span>
                  <span style={{ color: '#666666' }}>
                    {l.hoeveelheid != null
                      ? `${l.hoeveelheid.toLocaleString('nl-NL', { maximumFractionDigits: 2 })} ${l.eenheid ?? ''}`
                      : '—'}
                  </span>
                  <span style={{ color: '#666666' }}>{l.prijs != null ? euro(l.prijs) : '—'}</span>
                  <span style={{ textAlign: 'right', fontWeight: 700 }}>{euro(l.bedragExcl)}</span>
                  <span style={{ color: '#666666' }}>
                    {[l.station, l.plaats].filter(Boolean).join(' · ') || '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Panel>

      <SectieKop titel="Koppelhistorie" />
      <Panel style={{ padding: '20px 24px' }}>
        {koppelingen.length === 0 ? (
          <div style={{ fontSize: 13, color: '#888888' }}>Nog geen koppelingen.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {koppelingen.map((k) => (
              <div
                key={k.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 0',
                  borderTop: '1px solid #F0E8DC',
                  fontSize: 13,
                }}
              >
                <span style={{ flex: 1 }}>
                  {k.persoonNaam ? (
                    <PersoonLink persoonId={k.persoonId} naam={k.persoonNaam} />
                  ) : (
                    <span style={{ fontWeight: 700, color: '#111111' }}>Onbekend</span>
                  )}
                </span>
                <span
                  style={{ color: '#666666', fontFamily: "'Roboto Mono', monospace", fontSize: 12 }}
                >
                  {k.tot ? `${fmtDate(k.van)} – ${fmtDate(k.tot)}` : `sinds ${fmtDate(k.van)}`}
                </span>
                {!k.tot && <Badge tone="success">Actief</Badge>}
              </div>
            ))}
          </div>
        )}
      </Panel>

      {confirm && (
        <ConfirmModal
          title="Pas archiveren"
          message={
            pas.persoonNaam
              ? `Deze pas is nog gekoppeld aan ${pas.persoonNaam}. Ontkoppel de pas eerst via de persoon voordat je hem archiveert.`
              : `Pas ${pas.pasnummer} archiveren (uit gebruik)? De kostenhistorie blijft bewaard; je kunt de pas later terugzetten.`
          }
          confirmLabel="Archiveren"
          danger
          busy={archiveer.isPending}
          error={archErr}
          onConfirm={async () => {
            setArchErr('');
            try {
              await archiveer.mutateAsync(pas.id);
              setConfirm(false);
            } catch (e) {
              setArchErr(e instanceof ApiError ? e.message : 'Archiveren mislukt');
            }
          }}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
}
